import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminPageTitle from '../components/AdminPageTitle';
import { fetchProduct, updateProduct, updateProductStatus } from '../services/products';
import { getStorageUrl } from '../services/api';
import './ProductForm.css';

const ProductEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    subtitle: '',
    price: '',
    stock: '',
    status: 'active',
    featured: false,
    availability_type: '',
  });
  const [mainImage, setMainImage] = useState(null);
  const [mainImagePreview, setMainImagePreview] = useState(null);
  const [existingMainImageUrl, setExistingMainImageUrl] = useState(null);
  const [additionalImages, setAdditionalImages] = useState([]);
  const [additionalImagesPreview, setAdditionalImagesPreview] = useState([]);
  const [existingAdditionalImages, setExistingAdditionalImages] = useState([]);
  const [specifications, setSpecifications] = useState([]);
  const [productSpecificationText, setProductSpecificationText] = useState('');
  const [errors, setErrors] = useState({});
  const additionalImagesInputRef = useRef(null);

  const getImageUrl = (path) => getStorageUrl(path);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setInitialLoading(true);
      setFetchError(null);
      try {
        const product = await fetchProduct(id);
        if (cancelled) return;
        setFormData({
          name: product.name || '',
          description: product.description || '',
          subtitle: product.subtitle || '',
          price: product.price != null ? product.price : '',
          stock: product.stock != null ? product.stock : '',
          status: (product.status || 'active').toLowerCase(),
          featured: !!product.featured,
          availability_type: product.availability_type || '',
        });
        if (product.image) {
          setExistingMainImageUrl(getImageUrl(product.image));
          setMainImagePreview(getImageUrl(product.image));
        }
        const specs = product.specifications || product.variants || [];
        setSpecifications(
          specs.map((s) => ({
            id: s.id,
            spec_name: s.spec_name || '',
            spec_value: s.spec_value || '',
            price: s.price != null ? s.price : '',
            available: s.available !== false,
          }))
        );
        const prodSpec = product.product_specification;
        if (Array.isArray(prodSpec) && prodSpec.length > 0) {
          setProductSpecificationText(prodSpec.join('\n'));
        } else {
          setProductSpecificationText('');
        }
        const imgs = product.images || [];
        setExistingAdditionalImages(
          imgs
            .filter((img) => img.image_path)
            .map((img) => ({
              id: img.id,
              url: getImageUrl(img.image_path),
            }))
        );
      } catch (err) {
        if (!cancelled) {
          setFetchError(err.response?.data?.message || 'Failed to load product.');
        }
      } finally {
        if (!cancelled) setInitialLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleMainImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setMainImage(file);
      const reader = new FileReader();
      reader.onload = (ev) => setMainImagePreview(ev.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleAdditionalImagesChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setAdditionalImages((prev) => [...prev, ...files]);
    const previewPromises = files.map(
      (file) =>
        new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = (ev) => resolve(ev.target.result);
          reader.readAsDataURL(file);
        })
    );
    Promise.all(previewPromises).then((previews) => {
      setAdditionalImagesPreview((prev) => [...prev, ...previews]);
    });
    if (e.target) e.target.value = '';
  };

  const handleAddMoreImages = () => {
    if (additionalImagesInputRef.current) additionalImagesInputRef.current.click();
  };

  const removeAdditionalImage = (index) => {
    setAdditionalImages((prev) => prev.filter((_, i) => i !== index));
    setAdditionalImagesPreview((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSpecificationChange = (index, field, value) => {
    const next = [...specifications];
    next[index] = { ...next[index], [field]: value };
    setSpecifications(next);
  };

  const handleAddSpecification = () => {
    setSpecifications((prev) => [
      ...prev,
      { id: null, spec_name: '', spec_value: '', price: '', available: true },
    ]);
  };

  const handleRemoveSpecification = (index) => {
    setSpecifications((prev) => prev.filter((_, i) => i !== index));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Product name is required';
    if (!formData.description.trim()) newErrors.description = 'Product description is required';
    const hasMain = mainImage || existingMainImageUrl;
    const hasExtra = additionalImages.length > 0 || existingAdditionalImages.length > 0;
    if (!hasMain && !hasExtra) newErrors.images = 'At least one product image is required';
    specifications.forEach((spec, i) => {
      if (!(spec.spec_name || '').trim()) newErrors[`spec_name_${i}`] = 'Variant name is required';
      if (!(spec.spec_value || '').trim()) newErrors[`spec_value_${i}`] = 'Variant value is required';
      if (spec.price === '' || (spec.price != null && parseFloat(spec.price) < 0)) {
        newErrors[`spec_price_${i}`] = 'Valid price is required';
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const submitData = new FormData();
      submitData.append('name', formData.name);
      submitData.append('description', formData.description);
      if (formData.subtitle) submitData.append('subtitle', formData.subtitle);
      if (formData.price !== '') submitData.append('price', formData.price);
      if (formData.stock !== '') submitData.append('stock', formData.stock);
      submitData.append('status', formData.status);
      submitData.append('featured', formData.featured ? '1' : '0');
      submitData.append('availability_type', formData.availability_type || '');
      if (mainImage) submitData.append('image', mainImage);
      additionalImages.forEach((file) => submitData.append('images[]', file));
      specifications.forEach((spec, index) => {
        if (spec.id != null) submitData.append(`specifications[${index}][id]`, spec.id);
        submitData.append(`specifications[${index}][spec_name]`, spec.spec_name);
        submitData.append(`specifications[${index}][spec_value]`, spec.spec_value);
        submitData.append(`specifications[${index}][price]`, spec.price);
        submitData.append(`specifications[${index}][available]`, spec.available ? '1' : '0');
      });
      if (productSpecificationText.trim()) {
        productSpecificationText
          .split('\n')
          .map((line) => line.trim())
          .filter(Boolean)
          .forEach((line) => submitData.append('product_specification[]', line));
      }
      await updateProduct(id, submitData);
      alert('Product updated successfully.');
      navigate('/admin/products');
    } catch (err) {
      console.error('Error updating product:', err);
      alert(err.response?.data?.message || 'Failed to update product. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = async () => {
    if (!window.confirm('Are you sure you want to disable this product?')) return;
    try {
      await updateProductStatus(id, 'inactive');
      setFormData((prev) => ({ ...prev, status: 'inactive' }));
      alert('Product disabled successfully.');
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update status.');
    }
  };

  const displayMainPreview = mainImagePreview || existingMainImageUrl;

  if (initialLoading) {
    return (
      <div className="product-form-page">
        <AdminPageTitle title="Edit Product" />
        <div className="loading">Loading product...</div>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="product-form-page">
        <AdminPageTitle title="Edit Product" />
        <div className="page-header">
          <h1>Edit Product</h1>
          <button onClick={() => navigate('/admin/products')} className="back-btn">
            ← Back to Products
          </button>
        </div>
        <div className="products-error">{fetchError}</div>
      </div>
    );
  }

  return (
    <div className="product-form-page">
      <AdminPageTitle title="Edit Product" />
      <div className="page-header">
        <h1>Edit Product</h1>
        <button onClick={() => navigate('/admin/products')} className="back-btn">
          ← Back to Products
        </button>
      </div>

      <form onSubmit={handleSubmit} className="product-form" encType="multipart/form-data">
        <div className="form-section">
          <h2>Basic Information</h2>
          <div className="form-group">
            <label htmlFor="name">Product Title *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={errors.name ? 'error' : ''}
              placeholder="Enter product title"
            />
            {errors.name && <span className="error-message">{errors.name}</span>}
          </div>
          <div className="form-group">
            <label htmlFor="subtitle">Product Subtitle</label>
            <input
              type="text"
              id="subtitle"
              name="subtitle"
              value={formData.subtitle}
              onChange={handleChange}
              placeholder="Enter product subtitle"
            />
          </div>
          <div className="form-group">
            <label htmlFor="description">Product Description *</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="5"
              className={errors.description ? 'error' : ''}
              placeholder="Enter product description"
            />
            {errors.description && <span className="error-message">{errors.description}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="mainImage">Main Product Image</label>
            <input
              type="file"
              id="mainImage"
              accept="image/*"
              onChange={handleMainImageChange}
            />
            {displayMainPreview && (
              <div className="image-preview">
                <img src={displayMainPreview} alt="Main" />
                <p className="field-help-text">Upload a new file to replace the current image.</p>
              </div>
            )}
            {errors.images && <span className="error-message">{errors.images}</span>}
          </div>

          <div className="form-group">
            <label>Additional Product Images</label>
            <div className="image-upload-section">
              <input
                type="file"
                id="additionalImages"
                accept="image/*"
                multiple
                onChange={handleAdditionalImagesChange}
                ref={additionalImagesInputRef}
                style={{ display: 'none' }}
              />
              <button type="button" onClick={handleAddMoreImages} className="add-more-images-btn">
                + Add More Images
              </button>
              {(additionalImages.length > 0 || existingAdditionalImages.length > 0) && (
                <span className="images-count">
                  {existingAdditionalImages.length + additionalImages.length} image(s)
                </span>
              )}
            </div>
            {existingAdditionalImages.length > 0 && (
              <div className="images-preview-grid">
                {existingAdditionalImages.map((img) => (
                  <div key={img.id} className="image-preview-item">
                    <img src={img.url} alt="" />
                    <span className="existing-badge">Existing</span>
                  </div>
                ))}
                {additionalImagesPreview.map((preview, index) => (
                  <div key={`new-${index}`} className="image-preview-item">
                    <img src={preview} alt="" />
                    <button
                      type="button"
                      onClick={() => removeAdditionalImage(index)}
                      className="remove-image-btn"
                      title="Remove"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
            {existingAdditionalImages.length === 0 && additionalImagesPreview.length > 0 && (
              <div className="images-preview-grid">
                {additionalImagesPreview.map((preview, index) => (
                  <div key={index} className="image-preview-item">
                    <img src={preview} alt="" />
                    <button
                      type="button"
                      onClick={() => removeAdditionalImage(index)}
                      className="remove-image-btn"
                      title="Remove"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="price">Base Price (¥)</label>
              <input
                type="number"
                id="price"
                name="price"
                value={formData.price}
                onChange={handleChange}
                placeholder="0.00 (Yuan)"
                min="0"
                step="0.01"
              />
            </div>
            <div className="form-group">
              <label htmlFor="stock">Available Stock</label>
              <input
                type="number"
                id="stock"
                name="stock"
                value={formData.stock}
                onChange={handleChange}
                placeholder="0"
                min="0"
              />
            </div>
            <div className="form-group">
              <label htmlFor="status">Status</label>
              <select id="status" name="status" value={formData.status} onChange={handleChange}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="featured"
                checked={formData.featured}
                onChange={(e) => setFormData((prev) => ({ ...prev, featured: e.target.checked }))}
              />
              <span>Mark as Featured Product</span>
            </label>
            <p className="field-help-text">Featured products will be highlighted on the homepage</p>
          </div>

          <div className="form-group">
            <label htmlFor="availability_type">Availability Type</label>
            <select
              id="availability_type"
              name="availability_type"
              value={formData.availability_type}
              onChange={handleChange}
            >
              <option value="">— Not set —</option>
              <option value="shipped_abroad">Shipped from Abroad</option>
              <option value="available_locally">Available Locally</option>
              <option value="for_export">For Export</option>
            </select>
            <p className="field-help-text">Shows a badge on the storefront for this product.</p>
          </div>
        </div>

        <div className="form-section">
          <div className="section-header-inline">
            <h2>Product Variants</h2>
            <button type="button" onClick={handleAddSpecification} className="add-size-btn">
              + Add Variant
            </button>
          </div>
          {specifications.length > 0 ? (
            <div className="sizes-list">
              {specifications.map((spec, index) => (
                <div key={spec.id || `new-${index}`} className="size-item-form">
                  <div className="form-group">
                    <label>Variant Name *</label>
                    <input
                      type="text"
                      value={spec.spec_name}
                      onChange={(e) => handleSpecificationChange(index, 'spec_name', e.target.value)}
                      placeholder="e.g., Size, Weight"
                      className={errors[`spec_name_${index}`] ? 'error' : ''}
                    />
                    {errors[`spec_name_${index}`] && (
                      <span className="error-message">{errors[`spec_name_${index}`]}</span>
                    )}
                  </div>
                  <div className="form-group">
                    <label>Variant Value *</label>
                    <input
                      type="text"
                      value={spec.spec_value}
                      onChange={(e) => handleSpecificationChange(index, 'spec_value', e.target.value)}
                      placeholder="e.g., 50 x 10 (sqm)"
                      className={errors[`spec_value_${index}`] ? 'error' : ''}
                    />
                    {errors[`spec_value_${index}`] && (
                      <span className="error-message">{errors[`spec_value_${index}`]}</span>
                    )}
                  </div>
                  <div className="form-group">
                    <label>Variant Price (¥) *</label>
                    <input
                      type="number"
                      value={spec.price}
                      onChange={(e) => handleSpecificationChange(index, 'price', e.target.value)}
                      placeholder="0.00 (Yuan)"
                      min="0"
                      step="0.01"
                      className={errors[`spec_price_${index}`] ? 'error' : ''}
                    />
                    {errors[`spec_price_${index}`] && (
                      <span className="error-message">{errors[`spec_price_${index}`]}</span>
                    )}
                  </div>
                  <div className="form-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={spec.available !== false}
                        onChange={(e) => handleSpecificationChange(index, 'available', e.target.checked)}
                      />
                      Available
                    </label>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveSpecification(index)}
                    className="remove-size-btn"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="no-sizes">No variants. Click &quot;Add Variant&quot; to add.</p>
          )}
        </div>

        <div className="form-section">
          <h2>Product Specification (Technical Details)</h2>
          <div className="form-group">
            <label htmlFor="productSpecification">One item per line</label>
            <textarea
              id="productSpecification"
              rows="6"
              value={productSpecificationText}
              onChange={(e) => setProductSpecificationText(e.target.value)}
              placeholder="Material: Durable polyethylene (PE)&#10;UV Treatment: Up to 5 years"
            />
          </div>
        </div>

        <div className="form-actions">
          <button type="button" onClick={handleDisable} className="disable-btn">
            Disable Product
          </button>
          <div className="action-buttons">
            <button type="button" onClick={() => navigate('/admin/products')} className="cancel-btn">
              Cancel
            </button>
            <button type="submit" className="submit-btn" disabled={loading}>
              {loading ? 'Updating...' : 'Update Product'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ProductEdit;
